if (customElements && !customElements.get('pdp-gift-select')) {
  class PdpGiftSelect extends HTMLElement {
    constructor() {
      super();
      this.activeOption = this.querySelector('.js-pdp-gift-select-active-option');
      this.activeElement = this.querySelector('.js-pdp-gift-select-item-active');
      this.dropdown = this.querySelector('.js-pdp-gift-select-dropdown');
      // Prefer form by explicit data attribute on the component
      this.formId = this.dataset.formId;
      this.form = this.formId ? document.getElementById(this.formId) : (this.closest('form') || null);
      this.formInputProductId = this.form ? this.form.querySelector('input[name="id"]') : null;

      // Hidden inputs for adding gift as bundle line item
      this.giftItemInput = null; // name: items[1][id]
      this.giftQtyInput = null; // name: items[1][quantity]
      this.giftFlagInput = null; // name: items[1][properties][_isGift]
      this.packFlagInput = null; // name: properties[_pack] on main product

      this.isOpen = false;
    }

    connectedCallback() {
      this.init();
      this.attachEvents();
    }

    init() {
      // Check if this component belongs to an active pack
      const isActivePack = this.closest('.product-pack-selector__item--active') !== null;
      
      // Check if there are any gift items in the dropdown
      const firstItem = this.dropdown.querySelector('.js-pdp-gift-select-item');
      const hasGifts = firstItem !== null;
      
      // Only create/configure gift inputs if this component has gifts AND is active pack
      if (this.form && hasGifts && isActivePack) {
        // Find or create the single gift inputs
        const existingGiftInputs = this.form.querySelectorAll('input[name="items[1][id]"]');
        if (existingGiftInputs.length > 1) {
          // Keep the first, remove duplicates
          existingGiftInputs.forEach((el, idx) => { if (idx > 0) el.remove(); });
        }
        this.giftItemInput = this.form.querySelector('input[name="items[1][id]"]');
        if (!this.giftItemInput) {
          this.giftItemInput = document.createElement('input');
          this.giftItemInput.type = 'hidden';
          this.giftItemInput.name = 'items[1][id]';
          this.giftItemInput.className = 'js-pdp-gift-input';
          this.form.appendChild(this.giftItemInput);
        }

        // Ensure a quantity of 1 for the additional item
        const existingQtyInputs = this.form.querySelectorAll('input[name="items[1][quantity]"]');
        if (existingQtyInputs.length > 1) {
          existingQtyInputs.forEach((el, idx) => { if (idx > 0) el.remove(); });
        }
        this.giftQtyInput = this.form.querySelector('input[name="items[1][quantity]"]');
        if (!this.giftQtyInput) {
          this.giftQtyInput = document.createElement('input');
          this.giftQtyInput.type = 'hidden';
          this.giftQtyInput.name = 'items[1][quantity]';
          this.giftQtyInput.value = '1';
          this.giftQtyInput.className = 'js-pdp-gift-qty';
          this.form.appendChild(this.giftQtyInput);
        } else {
          this.giftQtyInput.value = '1';
        }

        const existingGiftFlagInputs = this.form.querySelectorAll('input[name="items[1][properties][_isGift_1]"]');
        if (existingGiftFlagInputs.length > 1) {
          existingGiftFlagInputs.forEach((el, idx) => { if (idx > 0) el.remove(); });
        }
        this.giftFlagInput = this.form.querySelector('input[name="items[1][properties][_isGift_1]"]');
        if (!this.giftFlagInput) {
          this.giftFlagInput = document.createElement('input');
          this.giftFlagInput.type = 'hidden';
          this.giftFlagInput.name = 'items[1][properties][_isGift_1]';
          this.giftFlagInput.value = true;
          this.giftFlagInput.className = 'js-pdp-gift-flag';
          this.form.appendChild(this.giftFlagInput);
        }

        // Ensure main product carries the _pack flag
        const existingPackFlags = this.form.querySelectorAll('input[name="properties[_pack_bf]"]');
        if (existingPackFlags.length > 1) {
          existingPackFlags.forEach((el, idx) => { if (idx > 0) el.remove(); });
        }
        this.packFlagInput = this.form.querySelector('input[name="properties[_pack_bf]"]');
        if (!this.packFlagInput) {
          this.packFlagInput = document.createElement('input');
          this.packFlagInput.type = 'hidden';
          this.packFlagInput.name = 'properties[_pack_bf]';
          this.packFlagInput.value = true;
          this.packFlagInput.className = 'js-pdp-pack-flag';
          this.form.appendChild(this.packFlagInput);
        } else {
          this.packFlagInput.value =true;
        }
      }

      // Select the first item for display purposes
      if (firstItem) {
        this.selectItem(firstItem);
        
        // Get variant ID directly from the item after selection
        const variantId = firstItem.dataset.productVariantId || this.activeVariantId;
        
        // CRITICAL: Only set form values if this belongs to the active pack
        if (isActivePack && variantId && parseInt(variantId) > 0) {
          // Set values immediately for active pack
          requestAnimationFrame(() => {
            // Double-check we're still the active pack and variant is still valid
            const stillActive = this.closest('.product-pack-selector__item--active') !== null;
            const currentVariantId = this.activeVariantId || variantId;
            if (stillActive && currentVariantId && parseInt(currentVariantId) > 0) {
              this.setGiftInputsValue(currentVariantId);
            }
          });
        } else if (!isActivePack) {
          // Inactive pack - ensure inputs are disabled/cleared
          this.disableGiftInputs();
        }
      } else {
        // This component has no gifts - if active pack has no gift selector, disable inputs
        // Check if the active pack item has a gift selector component
        if (this.form) {
          const productPackSelector = this.closest('.product-pack-selector');
          if (productPackSelector) {
            const activePackItem = productPackSelector.querySelector('.product-pack-selector__item--active');
            const activePackHasGiftSelector = activePackItem ? activePackItem.querySelector('pdp-gift-select') !== null : false;
            
            if (!activePackHasGiftSelector) {
              // Active pack has no gift selector - disable all gift inputs
              this.disableGiftInputs();
            }
          }
        }
      }
    }

    get activeVariantId() {
      return this.activeOption.dataset.selectVariantId || 0;
    }

    set activeVariantId(value) {
      this.activeOption.dataset.selectVariantId = value;
      return value;
    }

    // We do NOT change the main product variant id here; only manage gift inputs

    setGiftInputsValue = (variantId) => {
      if (!this.form) return;
      // Only set values if we have a valid variant ID
      if (!variantId || parseInt(variantId) <= 0) {
        this.disableGiftInputs();
        return;
      }
      
      // Ensure inputs exist - they might not be created yet if this component has gifts
      if (!this.giftItemInput) {
        this.giftItemInput = this.form.querySelector('input[name="items[1][id]"]');
      }
      if (!this.giftQtyInput) {
        this.giftQtyInput = this.form.querySelector('input[name="items[1][quantity]"]');
      }
      if (!this.giftFlagInput) {
        this.giftFlagInput = this.form.querySelector('input[name="items[1][properties][_isGift_1]"]');
      }
      
      // Create inputs if they don't exist (they should exist if hasGifts was true, but just in case)
      if (!this.giftItemInput) {
        this.giftItemInput = document.createElement('input');
        this.giftItemInput.type = 'hidden';
        this.giftItemInput.name = 'items[1][id]';
        this.giftItemInput.className = 'js-pdp-gift-input';
        this.form.appendChild(this.giftItemInput);
      }
      if (!this.giftQtyInput) {
        this.giftQtyInput = document.createElement('input');
        this.giftQtyInput.type = 'hidden';
        this.giftQtyInput.name = 'items[1][quantity]';
        this.giftQtyInput.value = '1';
        this.giftQtyInput.className = 'js-pdp-gift-qty';
        this.form.appendChild(this.giftQtyInput);
      }
      if (!this.giftFlagInput) {
        this.giftFlagInput = document.createElement('input');
        this.giftFlagInput.type = 'hidden';
        this.giftFlagInput.name = 'items[1][properties][_isGift_1]';
        this.giftFlagInput.value = true;
        this.giftFlagInput.className = 'js-pdp-gift-flag';
        this.form.appendChild(this.giftFlagInput);
      }
      
      // Now set the values
      this.giftItemInput.value = variantId;
      this.giftItemInput.disabled = false;
      this.giftQtyInput.value = '1';
      this.giftQtyInput.disabled = false;
      this.giftFlagInput.disabled = false;
    };

    disableGiftInputs = () => {
      if (!this.form) return;
      // Disable gift inputs if they exist to prevent form submission errors
      const giftItemInput = this.form.querySelector('input[name="items[1][id]"]');
      const giftQtyInput = this.form.querySelector('input[name="items[1][quantity]"]');
      const giftFlagInput = this.form.querySelector('input[name="items[1][properties][_isGift_1]"]');
      
      if (giftItemInput) {
        giftItemInput.disabled = true;
        giftItemInput.value = '';
      }
      if (giftQtyInput) {
        giftQtyInput.disabled = true;
      }
      if (giftFlagInput) {
        giftFlagInput.disabled = true;
      }
    };

    attachEvents = () => {
      this.activeOption.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleDropdown();
      });

      this.dropdown.addEventListener('click', (e) => {
        const item = e.target.closest('.js-pdp-gift-select-item');
        if (!item) return;

        e.preventDefault();
        this.selectItem(item);
        this.setGiftInputsValue(this.activeVariantId);
        this.closeDropdown();

      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeDropdown();
        }
      });
    };

    selectItem = (item) => {
      // Ensure item belongs to this component's dropdown
      if (!this.dropdown.contains(item)) {
        return;
      }

      // Remove is-selected from all items in this dropdown
      const allItems = this.dropdown.querySelectorAll('.js-pdp-gift-select-item');
      allItems.forEach(el => el.classList.remove('is-selected'));

      // Add is-selected to the selected item
      item.classList.add('is-selected');
      this.activeElement.innerHTML = item.innerHTML;
      this.activeVariantId = item.dataset.productVariantId;
    };

    toggleDropdown = () => {
      this.isOpen ? this.closeDropdown() : this.openDropdown();
    };

    openDropdown = () => {
      this.isOpen = true;
      this.classList.add('is-open');
    };

    closeDropdown = () => {
      this.isOpen = false;
      this.classList.remove('is-open');
    };
  }

  customElements.define('pdp-gift-select', PdpGiftSelect);
}